import { Box, Link, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Region } from "../../types";

interface ReferenceImageCardProps {
  imageSrc: string;
  onChangeClick: () => void;
  regions?: Region[];
  activeRegionId?: string;
  onRegionClick?: (regionId: string) => void;
}

interface ImageBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

export const ReferenceImageCard = ({
  imageSrc,
  onChangeClick,
  regions,
  activeRegionId,
  onRegionClick,
}: ReferenceImageCardProps) => {
  const { t } = useTranslation();
  const [imageBounds, setImageBounds] = useState<ImageBounds | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const computeImageBounds = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const img = container.querySelector("img");
    if (!img?.naturalWidth || !img.naturalHeight) return;

    const containerW = container.clientWidth;
    const containerH = container.clientHeight;
    const imgRatio = img.naturalWidth / img.naturalHeight;
    const containerRatio = containerW / containerH;

    let renderW: number;
    let renderH: number;
    if (imgRatio > containerRatio) {
      renderW = containerW;
      renderH = containerW / imgRatio;
    } else {
      renderH = containerH;
      renderW = containerH * imgRatio;
    }

    setImageBounds({
      left: (containerW - renderW) / 2,
      top: (containerH - renderH) / 2,
      width: renderW,
      height: renderH,
    });
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let rafId = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(computeImageBounds);
    });
    observer.observe(container);
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [computeImageBounds]);

  return (
    <Box>
      <Box
        ref={containerRef}
        sx={{
          position: "relative",
          width: "100%",
          height: 240,
          backgroundColor: "var(--alias-surface-low, #f2f2f2)",
          overflow: "hidden",
        }}
      >
        <img
          src={imageSrc}
          alt={t("ldp_explore_image_results_reference_alt", "Reference image")}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
          onLoad={computeImageBounds}
        />
        {/* Dim overlay when showing region outlines */}
        {regions && regions.length > 0 && imageBounds && (
          <Box
            sx={{
              position: "absolute",
              left: imageBounds.left,
              top: imageBounds.top,
              width: imageBounds.width,
              height: imageBounds.height,
              backgroundColor: "rgba(0,0,0,0.40)",
              pointerEvents: "none",
            }}
          />
        )}
        {/* Region outlines — positioned relative to actual image bounds */}
        {imageBounds &&
          regions?.map((r) => {
            const isActive = r.id === activeRegionId;
            return (
              <Box
                key={r.id}
                onClick={() => onRegionClick?.(r.id)}
                sx={{
                  position: "absolute",
                  left: imageBounds.left + (r.x / 100) * imageBounds.width,
                  top: imageBounds.top + (r.y / 100) * imageBounds.height,
                  width: (r.width / 100) * imageBounds.width,
                  height: (r.height / 100) * imageBounds.height,
                  border: `1.5px solid ${r.color}`,
                  boxSizing: "border-box",
                  backgroundColor: isActive ? `${r.color}1A` : "transparent",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: -1,
                    left: -1,
                    padding: "0 3px",
                    backgroundColor: r.color,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'var(--font-family, "AktivGrotesk")',
                      fontSize: "7px",
                      color: "var(--global-neutral-white, #fff)",
                      lineHeight: 1.4,
                    }}
                  >
                    {r.label}
                  </Typography>
                </Box>
              </Box>
            );
          })}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "11px 14px",
        }}
      >
        <Typography
          sx={{
            fontSize: "var(--body-b3-12-regular-size, 12px)",
            fontFamily: 'var(--font-family, "AktivGrotesk")',
            fontWeight: 500,
            color: "#000",
          }}
        >
          {t("ldp_explore_image_results_reference_image", "Reference Image")}
        </Typography>
        <Link
          component="button"
          onClick={onChangeClick}
          underline="none"
          sx={{
            fontSize: "var(--body-b3-12-regular-size, 12px)",
            fontWeight: 400,
            color: "var(--alias-text-on-white-brand, #009FE4)",
            cursor: "pointer",
          }}
        >
          {t("ldp_explore_image_results_change", "Change")}
        </Link>
      </Box>
    </Box>
  );
};
